// Claves que agrega System.Text.Json cuando se serializa con ReferenceHandler.Preserve.
const ID_KEY = '$id'
const REF_KEY = '$ref'
const VALUES_KEY = '$values'

const esObjetoPlano = (valor: unknown): valor is Record<string, unknown> =>
  valor !== null && typeof valor === 'object' && !Array.isArray(valor)

/** Preserve emite los identificadores como string, pero se acepta number por las dudas. */
const aClaveReferencia = (valor: unknown): string | undefined => {
  if (typeof valor === 'string' || typeof valor === 'number') {
    return String(valor)
  }

  return undefined
}

/**
 * Aplana la serialización `ReferenceHandler.Preserve` del backend (Program.cs).
 *
 * Preserve envuelve TODA lista como `{ $id, $values: [...] }`, marca cada objeto con `$id` y,
 * cuando un objeto ya salió antes en la misma respuesta, lo reemplaza por `{ $ref }`. El front
 * espera listas y objetos planos, así que hay que deshacer las tres cosas.
 *
 * Por qué se resuelve el `$ref` en vez de saltarlo: la versión anterior ignoraba la clave y
 * devolvía el resto del objeto, así que un `{"$ref":"12"}` (que no tiene más claves) se
 * convertía en `{}` y la pantalla pintaba una fila en blanco, sin error a la vista. Ahora se
 * devuelve el objeto original; si la referencia no se puede resolver se lanza, porque un dato
 * que no se puede reconstruir es un fallo de contrato y tiene que verse.
 */
export const unwrapPreserve = (valor: unknown): unknown => {
  // El mapa se crea por llamada: los `$id` solo son únicos dentro de una misma respuesta.
  const porId = new Map<string, unknown>()

  const recorrer = (nodo: unknown): unknown => {
    if (Array.isArray(nodo)) {
      return nodo.map(recorrer)
    }

    if (!esObjetoPlano(nodo)) {
      return nodo
    }

    const ref = aClaveReferencia(nodo[REF_KEY])

    if (ref !== undefined) {
      // Preserve siempre escribe el `$id` antes que cualquier `$ref` que lo apunte; si falta,
      // la respuesta viene rota y devolver un objeto vacío sería esconder el problema.
      if (!porId.has(ref)) {
        throw new Error(`Respuesta inválida: el $ref "${ref}" no tiene ningún $id que lo defina`)
      }

      return porId.get(ref)
    }

    const id = aClaveReferencia(nodo[ID_KEY])
    const valores = nodo[VALUES_KEY]

    if (Array.isArray(valores)) {
      const lista: unknown[] = []

      // Se registra antes de recorrer los hijos: si el grafo tiene un ciclo que vuelve a esta
      // misma lista, el `$ref` la encuentra ya creada en vez de recursar para siempre.
      if (id !== undefined) {
        porId.set(id, lista)
      }

      for (const item of valores) {
        lista.push(recorrer(item))
      }

      return lista
    }

    const plano: Record<string, unknown> = {}

    if (id !== undefined) {
      porId.set(id, plano)
    }

    for (const clave of Object.keys(nodo)) {
      if (clave === ID_KEY) {
        continue
      }

      plano[clave] = recorrer(nodo[clave])
    }

    return plano
  }

  return recorrer(valor)
}
